import React from "react";
import { Stack, Table, Switch, Text, Group, Select, Box } from "@mantine/core";
import {
  BusinessHoursData,
  DayHours,
  QUESTION_KEYS,
} from "../types/onboarding";
import styles from "./BusinessHoursStepContent.module.css";

// Generate time options in 15-minute increments with AM/PM format
const generateTimeOptions = () => {
  const options: { value: string; label: string }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const hour24 = hour.toString().padStart(2, "0");
      const min = minute.toString().padStart(2, "0");
      const value = `${hour24}:${min}`;

      // Format for display (12-hour with AM/PM)
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? "AM" : "PM";
      const label = `${hour12}:${min} ${ampm}`;

      options.push({ value, label });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Calculate duration between two times in hours
const calculateDuration = (startTime: string, endTime: string): string => {
  if (!startTime || !endTime) return "";
  const [startHour, startMin] = startTime.split(":").map(Number);
  const [endHour, endMin] = endTime.split(":").map(Number);

  let startMinutes = startHour * 60 + startMin;
  let endMinutes = endHour * 60 + endMin;

  // Handle overnight shifts
  if (endMinutes <= startMinutes) {
    endMinutes += 24 * 60;
  }

  const durationMinutes = endMinutes - startMinutes;
  const hours = durationMinutes / 60;

  if (hours === 1) return "1 hour";
  return `${hours} hours`;
};

type DayKey = keyof BusinessHoursData;

const DAYS: {
  key: DayKey;
  label: string;
  shortLabel: string;
  isOpenKey: string;
  openTimeKey: string;
  endTimeKey: string;
}[] = [
  {
    key: "sunday",
    label: "Sunday",
    shortLabel: "Sun",
    isOpenKey: QUESTION_KEYS.SUNDAY_IS_OPEN,
    openTimeKey: QUESTION_KEYS.SUNDAY_OPEN_TIME,
    endTimeKey: QUESTION_KEYS.SUNDAY_END_TIME,
  },
  {
    key: "monday",
    label: "Monday",
    shortLabel: "Mon",
    isOpenKey: QUESTION_KEYS.MONDAY_IS_OPEN,
    openTimeKey: QUESTION_KEYS.MONDAY_OPEN_TIME,
    endTimeKey: QUESTION_KEYS.MONDAY_END_TIME,
  },
  {
    key: "tuesday",
    label: "Tuesday",
    shortLabel: "Tues",
    isOpenKey: QUESTION_KEYS.TUESDAY_IS_OPEN,
    openTimeKey: QUESTION_KEYS.TUESDAY_OPEN_TIME,
    endTimeKey: QUESTION_KEYS.TUESDAY_END_TIME,
  },
  {
    key: "wednesday",
    label: "Wednesday",
    shortLabel: "Weds",
    isOpenKey: QUESTION_KEYS.WEDNESDAY_IS_OPEN,
    openTimeKey: QUESTION_KEYS.WEDNESDAY_OPEN_TIME,
    endTimeKey: QUESTION_KEYS.WEDNESDAY_END_TIME,
  },
  {
    key: "thursday",
    label: "Thursday",
    shortLabel: "Thurs",
    isOpenKey: QUESTION_KEYS.THURSDAY_IS_OPEN,
    openTimeKey: QUESTION_KEYS.THURSDAY_OPEN_TIME,
    endTimeKey: QUESTION_KEYS.THURSDAY_END_TIME,
  },
  {
    key: "friday",
    label: "Friday",
    shortLabel: "Fri",
    isOpenKey: QUESTION_KEYS.FRIDAY_IS_OPEN,
    openTimeKey: QUESTION_KEYS.FRIDAY_OPEN_TIME,
    endTimeKey: QUESTION_KEYS.FRIDAY_END_TIME,
  },
  {
    key: "saturday",
    label: "Saturday",
    shortLabel: "Sat",
    isOpenKey: QUESTION_KEYS.SATURDAY_IS_OPEN,
    openTimeKey: QUESTION_KEYS.SATURDAY_OPEN_TIME,
    endTimeKey: QUESTION_KEYS.SATURDAY_END_TIME,
  },
];

interface BusinessHoursStepContentProps {
  data: BusinessHoursData;
  onChange: (
    data: Partial<BusinessHoursData>,
    questionKey?: string,
    questionValue?: string,
    answerValue?: string,
  ) => void;
}

export default function BusinessHoursStepContent({
  data,
  onChange,
}: BusinessHoursStepContentProps) {
  const handleToggle = (
    dayKey: DayKey,
    dayLabel: string,
    isOpenKey: string,
    openTimeKey: string,
    endTimeKey: string,
  ) => {
    const currentDay = data[dayKey];
    const newIsOpen = !currentDay.isOpen;

    // Set default times when turning on (09:00 - 17:00)
    const defaultStartTime = "09:00";
    const defaultEndTime = "17:00";

    // Determine the times to use
    // Always set to defaults (even when off, though it doesn't matter)
    const startTime = defaultStartTime;
    const endTime = defaultEndTime;

    const updatedDay: DayHours = {
      ...currentDay,
      isOpen: newIsOpen,
      startTime,
      endTime,
    };

    // Update the state with the new day data
    // Always save the isOpen state (true or false)
    onChange(
      { [dayKey]: updatedDay } as Partial<BusinessHoursData>,
      isOpenKey,
      `${dayLabel} Is Open`,
      String(newIsOpen),
    );

    // If turning on, save the default times
    if (newIsOpen) {
      // Save start time
      onChange(
        { [dayKey]: updatedDay } as Partial<BusinessHoursData>,
        openTimeKey,
        `${dayLabel} Open Time`,
        startTime,
      );

      // Save end time
      onChange(
        { [dayKey]: updatedDay } as Partial<BusinessHoursData>,
        endTimeKey,
        `${dayLabel} End Time`,
        endTime,
      );
    }
    // When turning off, we've already saved the isOpen state as false
    // Times are set to defaults but not saved (since day is off)
  };

  const handleStartTimeChange = (
    dayKey: DayKey,
    dayLabel: string,
    openTimeKey: string,
    value: string,
  ) => {
    const currentDay = data[dayKey];
    onChange(
      {
        [dayKey]: {
          ...currentDay,
          startTime: value,
        },
      } as Partial<BusinessHoursData>,
      openTimeKey,
      `${dayLabel} Open Time`,
      value,
    );
  };

  const handleEndTimeChange = (
    dayKey: DayKey,
    dayLabel: string,
    endTimeKey: string,
    value: string,
  ) => {
    const currentDay = data[dayKey];
    onChange(
      {
        [dayKey]: {
          ...currentDay,
          endTime: value,
        },
      } as Partial<BusinessHoursData>,
      endTimeKey,
      `${dayLabel} End Time`,
      value,
    );
  };

  return (
    <Stack gap="md" className={styles.container}>
      <Box mb="xs">
        <Text size="sm" c="dimmed">
          <Text span c="red" mr={4}>
            *
          </Text>
          At least one day must be open with start and end times
        </Text>
      </Box>
      <Box className={styles.tableWrapper}>
        <Table className={styles.table} withRowBorders={false}>
          <Table.Tbody>
            {DAYS.map((day) => {
              const dayData = data[day.key];
              const duration = dayData.isOpen
                ? calculateDuration(dayData.startTime, dayData.endTime)
                : "";

              return (
                <Table.Tr key={day.key} className={styles.row}>
                  <Table.Td className={styles.dayColumn}>
                    <Text fw={500} className={styles.dayLabel}>
                      {day.label}
                    </Text>
                    <Text fw={500} className={styles.dayLabelMobile}>
                      {day.shortLabel}
                    </Text>
                  </Table.Td>
                  <Table.Td className={styles.statusColumn}>
                    <Group gap="sm" wrap="nowrap">
                      <Switch
                        checked={dayData.isOpen}
                        onChange={() =>
                          handleToggle(
                            day.key,
                            day.label,
                            day.isOpenKey,
                            day.openTimeKey,
                            day.endTimeKey,
                          )
                        }
                        size="lg"
                        className={styles.statusSwitch}
                      />
                      <Text
                        c={dayData.isOpen ? "dark" : "dimmed"}
                        className={styles.statusText}
                      >
                        {dayData.isOpen ? "Open" : "Closed"}
                      </Text>
                    </Group>
                  </Table.Td>
                  {dayData.isOpen ? (
                    <>
                      <Table.Td className={styles.timeColumn}>
                        <Group gap="sm" wrap="nowrap" align="center">
                          <Select
                            data={TIME_OPTIONS}
                            value={dayData.startTime}
                            onChange={(value) =>
                              handleStartTimeChange(
                                day.key,
                                day.label,
                                day.openTimeKey,
                                value || "",
                              )
                            }
                            size="sm"
                            className={styles.timeSelect}
                            searchable
                            allowDeselect={false}
                            comboboxProps={{ withinPortal: true }}
                          />
                          <Text c="dimmed" className={styles.timeSeparator}>
                            —
                          </Text>
                          <Select
                            data={TIME_OPTIONS}
                            value={dayData.endTime}
                            onChange={(value) =>
                              handleEndTimeChange(
                                day.key,
                                day.label,
                                day.endTimeKey,
                                value || "",
                              )
                            }
                            size="sm"
                            className={styles.timeSelect}
                            searchable
                            allowDeselect={false}
                            comboboxProps={{ withinPortal: true }}
                          />
                        </Group>
                      </Table.Td>
                      <Table.Td className={styles.durationColumn}>
                        <Text
                          c="dimmed"
                          size="sm"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {duration}
                        </Text>
                      </Table.Td>
                    </>
                  ) : (
                    <Table.Td colSpan={2} />
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Box>
    </Stack>
  );
}
